// @flow
import React, { memo, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import { Alert } from '@material-ui/lab';
import { confirm, showAlert } from 'actions/app';
import {
  removeAttachmentIds,
  setAttachments,
  setExcludedTemplateAttachments
} from 'actions/bulkEmail';
import clsx from 'clsx';
import { getAttemptsFormKeys } from 'features/bulk-email/sequences/BulkSequences.utils';
import { BulkEmailDrawerAttempts } from 'features/bulk-email/sequences/components/BulkEmailDrawerAttempts';
import { BulkEmailSectionsCollapser } from 'features/bulk-email/sequences/components/BulkEmailSectionsCollapser';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { useUserContext } from 'providers/UserProvider';
import strings from 'strings';
import FPHint from 'UI/components/atoms/FPHint';
import FPIcon from 'UI/components/atoms/FPIcon';
import SelectBox from 'UI/components/atoms/SelectBox';
import Text from 'UI/components/atoms/Text';
import TextBox from 'UI/components/atoms/TextBox';
import { When } from 'UI/components/atoms/When';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import FileUploaderOutlined from 'UI/components/molecules/FileUploaderOutlined';
import FPRadioGroup from 'UI/components/molecules/FPRadioGroup';
import FPTextEditor from 'UI/components/molecules/FPTextEditor';
import TemplateDropDown from 'UI/components/molecules/TemplateDropDown';
import { tinymceBulkConfig } from 'UI/constants/config';
import {
  BulkRecruitingOptions,
  BulkScopes,
  registerMultipleCheckboxOptions
} from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import { EntityType } from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { SvgArrowRight } from 'UI/res/icons/filled';
import { idOptionSelected } from 'UI/utils';
import { LabelRenderers, OptionRenderers } from 'UI/utils/renderers';

import { BulkFormFieldGrid } from './components/BulkFormFieldGrid';
import { BulkTypeOptions } from './components/BulkTypeOptions';
import { BulkBlockOptions, BulkDrawerFormFieldsMap } from './fields';
import { useStyles } from './styles';

const getTextColor = err => (err ? 'error' : 'initial');

const FORM_NAMES_MAP = [
  { key: BulkDrawerFormFieldsMap.Subject.key, valueKey: 'subject' },
  { key: BulkDrawerFormFieldsMap.EmailBody.key, valueKey: 'html' }
];

const {
  sendThroughOutlook: sendThroughOutlookCopies,
  fields: fieldsCopies,
  validations: validationsCopies,
  warnings,
  signatureInfo
} = strings.bulkEmails.forms;

const { sequences: sequencesDialogsCopies } = strings.bulkEmails.dialogs;

const OUTLOOK_SUPPORT_URL =
  window.GPAC_ENV?.OUTLOOK_PROVIDER_SUPPORT_URL ||
  process.env.REACT_APP_OUTLOOK_PROVIDER_SUPPORT_URL;

const BULK_SERVICE_EXPLANATION =
  window.GPAC_ENV?.BULK_SERVICE_EXPLANATION ||
  process.env.REACT_APP_BULK_SERVICE_EXPLANATION ||
  warnings.availableQuota;

const EMAIL_VARIANT_GENERATOR_URL =
  window.GPAC_ENV?.EMAIL_VARIANT_GENERATOR_URL || process.env.REACT_APP_EMAIL_VARIANT_GENERATOR_URL;

const getBulkableOptions = options => options.filter(opt => opt.bulkable_items !== 0);

const getBlockResendCopy = (blockResend, templateId) => {
  const { adviceTexts } = fieldsCopies.blockResend;
  if (!templateId) return adviceTexts.disabled;
  return blockResend === 'true' ? adviceTexts.on : adviceTexts.off;
};

const getTypesValidations = message => ({
  required: message,
  validate: val => (val && Array.isArray(val) && val.length > 0) || message
});

const BulkEmailForm = ({
  bulkQuota,
  AIButton,
  marketingOptions = [],
  shouldComposeBulkSequence = false
}) => {
  const { id: userId } = useUserContext();
  const { errors, getValues, register, setValue, triggerValidation, unregister, watch } =
    useFormContext();
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const isBulkShowEmailVariantEnabled = checkIfFeatureFlagEnabled(
    FeatureFlags.BulkShowEmailVariant
  );

  const watchers = watch([
    BulkDrawerFormFieldsMap.Attempts.key,
    BulkDrawerFormFieldsMap.AllowMarketingCandidates.key,
    BulkDrawerFormFieldsMap.AllowRecruitHirings.key,
    BulkDrawerFormFieldsMap.BlockAssociatedCompanies.key,
    BulkDrawerFormFieldsMap.BlockCompanies.key,
    BulkDrawerFormFieldsMap.BlockResend.key,
    BulkDrawerFormFieldsMap.BlockSigned.key,
    BulkDrawerFormFieldsMap.BulkScope.key,
    BulkDrawerFormFieldsMap.EmailBody.key,
    BulkDrawerFormFieldsMap.GeneralBulkCategory.key,
    BulkDrawerFormFieldsMap.JobOrderRecruitment.key,
    BulkDrawerFormFieldsMap.MarketCandidates.key,
    BulkDrawerFormFieldsMap.RemovedAttachments.key,
    BulkDrawerFormFieldsMap.ResendTime.key,
    BulkDrawerFormFieldsMap.SendViaOutlook.key,
    BulkDrawerFormFieldsMap.SmartTags.key,
    BulkDrawerFormFieldsMap.Subject.key,
    BulkDrawerFormFieldsMap.To.key
  ]);

  const editorRef = useRef(null);
  const [uiState, setUiState] = useState({
    showForm: true
  });

  const classes = useStyles({ errors, showForm: uiState.showForm });

  const location = useLocation().pathname.split('/');
  const history = useHistory();
  const { emailBody, attachments, template } = useSelector(state => state.bulkEmail.domain);
  const dispatch = useDispatch();

  useEffect(() => {
    register(
      { name: BulkDrawerFormFieldsMap.To.key },
      { required: validationsCopies.searchProject.required }
    );
    register({ name: BulkDrawerFormFieldsMap.BlockResend.key, value: 'false' });
    register(
      { name: BulkDrawerFormFieldsMap.BulkScope.key, value: null },
      { required: validationsCopies.bulkType.required }
    );
    register({ name: BulkDrawerFormFieldsMap.Template.key });
    register({ name: BulkDrawerFormFieldsMap.SendViaOutlook.key, value: false });
    register({ name: BulkDrawerFormFieldsMap.RemovedAttachments.key, value: [] });
  }, [register]);

  useEffect(() => {
    if (!template?.id) {
      unregister(BulkDrawerFormFieldsMap.ResendTime.key);
      setValue(BulkDrawerFormFieldsMap.BlockResend.key, 'false');
    }
  }, [setValue, template.id, unregister]);

  useEffect(() => {
    if (template?.id) setValue(BulkDrawerFormFieldsMap.Subject.key, emailBody.subject, true);
  }, [emailBody.subject, setValue, template.id]);

  useEffect(() => {
    if (template?.id) setValue(BulkDrawerFormFieldsMap.EmailBody.key, emailBody.html, true);
  }, [emailBody.html, setValue, template.id]);

  const handleCheckOption = (name, checked) => setValue(name, checked);

  const handleEditorChange = value => setValue(BulkDrawerFormFieldsMap.EmailBody.key, value, true);
  const handleEditorFocus = () => {
    triggerValidation();
    toggleFormVisibility(false)();
  };

  const handleSelectSearchProject = (name, value) => {
    setValue(
      name,
      {
        id: value?.id,
        name: value?.name,
        bulkableItems: value?.bulkable_items
      },
      true
    );
  };

  const handleFilesChange = (filesData, alert) => {
    if (alert?.severity === 'error') {
      dispatch(showAlert(alert));
      return;
    }
    filesData.forEach(attachment => dispatch(setAttachments({ ...attachment })));
  };

  const handleRemoveAttachment = ({ remove, alert }) => {
    if (alert?.severity === 'error') {
      dispatch(showAlert(alert));
      return;
    }
    remove.type === 'id' &&
      setValue(BulkDrawerFormFieldsMap.RemovedAttachments.key, [
        remove.value,
        ...watchers?.removedAttachments
      ]);
    dispatch(removeAttachmentIds(remove));
    dispatch(setExcludedTemplateAttachments(remove.value));
  };

  const resetPromptIdIfNeeded = () => {
    const shouldResetPromptId = AIButton && getValues(BulkDrawerFormFieldsMap.AIPromptId.key);
    if (shouldResetPromptId) {
      setValue(BulkDrawerFormFieldsMap.AIPromptId.key, null);
    }
  };

  const handleResendChange = ({ target: { name, value } }) => {
    const resendTimeKey = BulkDrawerFormFieldsMap.ResendTime.key;
    setValue(name, value);
    value === 'true'
      ? register({ name: resendTimeKey }, { required: validationsCopies.resendTime.required })
      : unregister(resendTimeKey);
  };
  const toggleFormVisibility = show => () => setUiState(prev => ({ ...prev, showForm: show }));
  const handleSubjectChange = (name, value) => setValue(name, value, true);

  const setBulkType = (name, value) => {
    const getOptionsNames = options => options.map(option => option.name);
    resetPromptIdIfNeeded();

    const typesRegistration = {
      [BulkScopes.Marketing]: () => {
        register(
          { name: BulkDrawerFormFieldsMap.MarketCandidates.key },
          getTypesValidations(validationsCopies.marketCandidates.required)
        );
        registerMultipleCheckboxOptions({
          options: marketingOptions,
          register,
          shouldSetDefaultValue: true
        });
        unregister([
          BulkDrawerFormFieldsMap.GeneralBulkCategory.key,
          BulkDrawerFormFieldsMap.JobOrderRecruitment.key,
          ...getOptionsNames(BulkRecruitingOptions)
        ]);
      },
      [BulkScopes.Recruiting]: () => {
        register(
          { name: BulkDrawerFormFieldsMap.JobOrderRecruitment.key },
          getTypesValidations(validationsCopies.jobOrdersToRecruit.required)
        );
        registerMultipleCheckboxOptions({
          options: BulkRecruitingOptions,
          register,
          shouldSetDefaultValue: true
        });
        unregister([
          BulkDrawerFormFieldsMap.GeneralBulkCategory.key,
          BulkDrawerFormFieldsMap.MarketCandidates.key,
          ...getOptionsNames(marketingOptions)
        ]);
      },
      [BulkScopes.General]: () => {
        register(
          { name: BulkDrawerFormFieldsMap.GeneralBulkCategory.key },
          { required: validationsCopies.generalBulkCategory.required }
        );
        unregister([
          BulkDrawerFormFieldsMap.MarketCandidates.key,
          BulkDrawerFormFieldsMap.JobOrderRecruitment.key,
          ...getOptionsNames([...marketingOptions, ...BulkRecruitingOptions])
        ]);
      }
    };

    setValue(name, value, true);
    typesRegistration[value?.id] && typesRegistration[value?.id]();
  };

  const resetFormValuesWhenChangingBulkType = () => {
    const fieldsToReset = [
      BulkDrawerFormFieldsMap.Subject.key,
      BulkDrawerFormFieldsMap.EmailBody.key
    ];
    const attemptsKey = BulkDrawerFormFieldsMap.Attempts.key;
    const attemptsIds = getValues()[attemptsKey];

    fieldsToReset.forEach(field => {
      setValue(field, '');
    });

    const attemptsFormKeys = getAttemptsFormKeys(attemptsIds);
    attemptsFormKeys.forEach((formKey, index) => {
      const shouldUnregisterField = index > 0;
      if (shouldUnregisterField) {
        unregister(formKey);
      } else {
        setValue(`${formKey}.subject`, '');
        setValue(`${formKey}.emailBody`, '');
        setValue(`${formKey}.triggerDays`, null);
      }
    });
    setValue(attemptsKey, [attemptsIds[0]]);
  };

  const handleBulkTypeSelect = (name, value) => {
    const formValues = getValues();
    const shouldShowChangeBulkTypeDialog =
      formValues[BulkDrawerFormFieldsMap.BulkScope.key] &&
      formValues[BulkDrawerFormFieldsMap.Attempts.key];

    if (shouldShowChangeBulkTypeDialog) {
      dispatch(
        confirm({
          ...sequencesDialogsCopies.changeBulkType,
          severity: 'warning',
          withButtons: 'YesNo',
          onConfirm: ok => {
            if (!ok) return;
            resetFormValuesWhenChangingBulkType();
            setBulkType(name, value);
          }
        })
      );
    } else {
      setBulkType(name, value);
    }
  };

  const handleSelectGeneralCategory = (name, value) => setValue(name, value, true);
  const handleSelectBulkTypeDependentOption = (name, value) => {
    setValue(name, value, true);
    resetPromptIdIfNeeded();
  };

  const handleSmartTagSelect = (_, value) => editorRef.current.insertContent(value[0].value);

  /**
   * @param {String} - Input name to watch
   * @param {String} - The separation tha will be used by Array.join function
   * in order to create one from given array
   * @param {String} - Message shown in case array has no values
   */
  const renderOptionsAsOnelineText = (input, joint, notItemsMessage) =>
    (watchers[input] && watchers[input].map(item => item.title).join(joint)) || notItemsMessage;

  const ListboxComponent = !location.some(val => val === 'searchproject')
    ? React.forwardRef(({ children, ...rest }, ref) => (
        <div>
          <ul ref={ref} {...rest}>
            {children}
          </ul>
          <div
            role="button"
            tabIndex={0}
            className={classes.listboxSPButton}
            onMouseDown={() => history.push('searchproject')}
          >
            <div>Go to Search Projects list</div>
            <FPIcon
              component={SvgArrowRight}
              size={14}
              family="mini"
              className={classes.spButtonIcon}
              onSetColor={palette => palette.primary.main}
            />
          </div>
        </div>
      ))
    : undefined;

  return (
    <>
      <When condition={shouldComposeBulkSequence}>
        <Alert className={classes.beforeSending} severity="warning">
          {warnings.beforeSending}
        </Alert>
      </When>
      <BulkEmailSectionsCollapser
        shouldShowCollapseButton={!!watchers[BulkDrawerFormFieldsMap.Attempts.key]}
        title={shouldComposeBulkSequence ? fieldsCopies.title : ''}
      >
        <Grid
          container
          data-testid="bulk-main-form-container"
          onClick={toggleFormVisibility(true)}
          className={clsx(!uiState.showForm && classes.itemsSpacing)}
        >
          <BulkFormFieldGrid
            label={fieldsCopies.searchProject.label}
            field={
              uiState.showForm ? (
                <AutocompleteSelect
                  customClasses={{ groupLabel: classes.groupLabel }}
                  disabled={location.includes('searchproject') && location.includes('preview')}
                  error={!!errors.to}
                  errorText={errors?.to?.message}
                  filterOptions={getBulkableOptions}
                  getDataOnFocus
                  getOptionLabel={option => {
                    const bulkableCount = option.bulkableItems || option.bulkable_items;
                    return !!option.name && typeof bulkableCount === 'number'
                      ? `${option.name} (${bulkableCount})`
                      : '';
                  }}
                  getOptionSelected={idOptionSelected}
                  groupBy={option =>
                    option.created_by === userId
                      ? 'MY SEARCH PROJECTS'
                      : 'OTHER RECRUITERS PUBLIC SEARCH PROJECTS'
                  }
                  ListboxComponent={ListboxComponent}
                  name={BulkDrawerFormFieldsMap.To.key}
                  onSelect={handleSelectSearchProject}
                  placeholder={fieldsCopies.searchProject.placeholder}
                  placeholderAsLabel={false}
                  renderOption={OptionRenderers.searchProjects}
                  selectedValue={watchers.to}
                  typeahead
                  typeaheadLimit={20}
                  typeaheadParams={{
                    createdBy: null,
                    direction: 'desc',
                    onlyMine: false,
                    orderBy: 'created_by',
                    page: 1,
                    perPage: 20
                  }}
                  url={Endpoints.SearchProjects}
                />
              ) : (
                <Text
                  color={getTextColor(errors.to && !watchers.to?.name)}
                  variant="body1"
                  text={watchers.to?.name || fieldsCopies.searchProject.noValue}
                />
              )
            }
            shouldShowField={uiState.showForm}
          />
          <BulkFormFieldGrid
            label={fieldsCopies.bulkType.label}
            hint={fieldsCopies.bulkType.hint}
            field={
              uiState.showForm ? (
                <AutocompleteSelect
                  name={BulkDrawerFormFieldsMap.BulkScope.key}
                  placeholder={fieldsCopies.bulkType.placeholder}
                  placeholderAsLabel={false}
                  onSelect={handleBulkTypeSelect}
                  getOptionLabel={option =>
                    fieldsCopies.bulkType.typeAndDescription[option.title] || ''
                  }
                  selectedValue={watchers.bulkScope}
                  url={Endpoints.BulkEmailScope}
                  error={!!errors.bulkScope}
                  errorText={errors.bulkScope?.message}
                />
              ) : (
                <Text
                  color={getTextColor(errors.bulkScope && !watchers.bulkScope?.title)}
                  variant="body1"
                  text={
                    fieldsCopies.bulkType.typeAndDescription[watchers.bulkScope?.title] ||
                    fieldsCopies.bulkType.noValue
                  }
                />
              )
            }
            shouldShowField={uiState.showForm}
          />
          {watchers.bulkScope?.id === BulkScopes.General && (
            <BulkFormFieldGrid
              label={fieldsCopies.generalBulkCategory.label}
              field={
                uiState.showForm ? (
                  <div className={classes.flexColumn}>
                    <AutocompleteSelect
                      name={BulkDrawerFormFieldsMap.GeneralBulkCategory.key}
                      placeholder={fieldsCopies.generalBulkCategory.placeholder}
                      placeholderAsLabel={false}
                      onSelect={handleSelectGeneralCategory}
                      getOptionLabel={option =>
                        `${option.title} ${option.description && `(${option.description})`}`
                      }
                      selectedValue={watchers[BulkDrawerFormFieldsMap.GeneralBulkCategory.key]}
                      url={Endpoints.BulkEmailGeneralCategories}
                      error={!!errors[BulkDrawerFormFieldsMap.GeneralBulkCategory.key]}
                      errorText={errors[BulkDrawerFormFieldsMap.GeneralBulkCategory.key]?.message}
                    />
                    <Alert severity="warning">{warnings.generalCategory}</Alert>
                  </div>
                ) : (
                  <Text
                    color={getTextColor(
                      errors[BulkDrawerFormFieldsMap.GeneralBulkCategory.key] &&
                        !watchers[BulkDrawerFormFieldsMap.GeneralBulkCategory.key]
                    )}
                    variant="body1"
                    text={
                      errors[BulkDrawerFormFieldsMap.GeneralBulkCategory.key]?.message
                        ? fieldsCopies.generalBulkCategory.noValue
                        : `${watchers[BulkDrawerFormFieldsMap.GeneralBulkCategory.key]?.title} (${
                            watchers[BulkDrawerFormFieldsMap.GeneralBulkCategory.key]?.description
                          })`
                    }
                  />
                )
              }
              shouldShowField={uiState.showForm}
            />
          )}
          {watchers.bulkScope?.id === BulkScopes.Marketing && (
            <BulkFormFieldGrid
              label={fieldsCopies.candidatesToMarket.label}
              hint={fieldsCopies.candidatesToMarket.hint}
              field={
                uiState.showForm ? (
                  <div className={classes.flexColumn}>
                    <AutocompleteSelect
                      error={!!errors.marketCandidates}
                      errorText={errors.marketCandidates?.message}
                      getOptionLabel={LabelRenderers.marketCandidates || ''}
                      getOptionSelected={idOptionSelected}
                      multiple
                      name={BulkDrawerFormFieldsMap.MarketCandidates.key}
                      onSelect={handleSelectBulkTypeDependentOption}
                      placeholder={fieldsCopies.candidatesToMarket.placeholder}
                      selectedValue={watchers.marketCandidates || []}
                      typeahead
                      typeaheadLimit={25}
                      typeaheadParams={{
                        entityType: EntityType.Candidate,
                        hideNonEmployees: true
                      }}
                      url={Endpoints.Search}
                    />
                  </div>
                ) : (
                  <Text
                    color={getTextColor(errors.marketCandidates)}
                    variant="body1"
                    text={renderOptionsAsOnelineText(
                      'marketCandidates',
                      ', ',
                      fieldsCopies.candidatesToMarket.noValue
                    )}
                  />
                )
              }
              extraContent={
                <BulkTypeOptions
                  onCheckOption={handleCheckOption}
                  options={marketingOptions}
                  watchers={watchers}
                />
              }
              shouldShowField={uiState.showForm}
            />
          )}
          {watchers.bulkScope?.id === BulkScopes.Recruiting && (
            <BulkFormFieldGrid
              label={fieldsCopies.jobOrdersToRecruit.label}
              hint={fieldsCopies.jobOrdersToRecruit.hint}
              field={
                uiState.showForm ? (
                  <div className={classes.flexColumn}>
                    <AutocompleteSelect
                      error={!!errors.jobOrderRecruitment}
                      errorText={errors.jobOrderRecruitment?.message}
                      getOptionLabel={LabelRenderers.jobOrders || ''}
                      getOptionSelected={idOptionSelected}
                      multiple
                      name={BulkDrawerFormFieldsMap.JobOrderRecruitment.key}
                      onSelect={handleSelectBulkTypeDependentOption}
                      placeholder={fieldsCopies.jobOrdersToRecruit.placeholder}
                      selectedValue={watchers.jobOrderRecruitment || []}
                      typeahead
                      typeaheadLimit={25}
                      typeaheadParams={{
                        entityType: EntityType.Joborder,
                        hideNonEmployees: true
                      }}
                      url={Endpoints.Search}
                    />
                  </div>
                ) : (
                  <Text
                    color={getTextColor(!!errors.jobOrderRecruitment)}
                    variant="body1"
                    text={renderOptionsAsOnelineText(
                      BulkDrawerFormFieldsMap.JobOrderRecruitment.key,
                      ', ',
                      fieldsCopies.jobOrdersToRecruit.noValue
                    )}
                  />
                )
              }
              extraContent={
                <>
                  <div className={classes.recruitingHintContainer}>
                    <FPHint description={fieldsCopies.jobOrdersToRecruit.optionsHint} />
                  </div>
                  <BulkTypeOptions
                    onCheckOption={handleCheckOption}
                    options={BulkRecruitingOptions}
                    watchers={watchers}
                  />
                </>
              }
              shouldShowField={uiState.showForm}
            />
          )}
          <When condition={AIButton && uiState.showForm}>
            <Grid container item justify="flex-end" className={classes.buttonContainer}>
              {AIButton}
            </Grid>
          </When>
          <BulkFormFieldGrid
            label={fieldsCopies.template.label}
            field={
              uiState.showForm ? (
                <TemplateDropDown
                  enableNoTemplateOption
                  formFieldName={BulkDrawerFormFieldsMap.Template.key}
                  formNamesToSet={FORM_NAMES_MAP}
                  onSelectTemplate={resetPromptIdIfNeeded}
                  placeholder={fieldsCopies.template.placeholder}
                />
              ) : (
                <Text variant="body1" text={template?.name || fieldsCopies.template.noValue} />
              )
            }
            shouldShowField={uiState.showForm}
          />
          <BulkFormFieldGrid
            label={fieldsCopies.blockResend.label}
            hint={fieldsCopies.blockResend.hint}
            field={
              <FPRadioGroup
                name={BulkDrawerFormFieldsMap.BlockResend.key}
                options={BulkBlockOptions.resend}
                adviceText={getBlockResendCopy(watchers?.blockResend, template?.id)}
                onChange={handleResendChange}
                value={watchers.blockResend || ''}
                disabled={!template?.id}
                classes={{ root: classes.rootRadioGroup }}
              />
            }
            shouldShowField={uiState.showForm}
          />
          {watchers.blockResend === 'true' && template?.id && (
            <BulkFormFieldGrid
              label={fieldsCopies.resendTime.label}
              field={
                <SelectBox
                  name={BulkDrawerFormFieldsMap.ResendTime.key}
                  placeholder={fieldsCopies.resendTime.placeholder}
                  error={errors.resendTime}
                  options={BulkBlockOptions.intervals}
                  value={watchers?.resendTime || ''}
                  onSelect={(name, value) => setValue(name, value)}
                />
              }
              shouldShowField={uiState.showForm}
            />
          )}
          <Grid container direction="column">
            <Alert
              className={classes.quotaAlert}
              action={
                <Button
                  color="inherit"
                  size="small"
                  className={classes.outlookAlertAction}
                  href={OUTLOOK_SUPPORT_URL}
                  target="_blank"
                  variant="outlined"
                >
                  {sendThroughOutlookCopies.alertAction}
                </Button>
              }
              severity="info"
            >
              {strings.formatString(BULK_SERVICE_EXPLANATION, {
                ...bulkQuota
              })}
            </Alert>
          </Grid>
          <BulkFormFieldGrid
            label={fieldsCopies.subject.label}
            field={
              uiState.showForm ? (
                <TextBox
                  name={BulkDrawerFormFieldsMap.Subject.key}
                  placeholder={fieldsCopies.subject.placeholder}
                  value={watchers.subject || ''}
                  onChange={handleSubjectChange}
                  error={!!errors.subject}
                  errorText={errors.subject?.message}
                />
              ) : (
                <Text
                  color={getTextColor(errors.subject)}
                  variant="body1"
                  text={`${watchers.subject || fieldsCopies.subject.noValue}${
                    errors.subject ? ` (${errors.subject?.message})` : ''
                  }`}
                />
              )
            }
            shouldShowField={uiState.showForm}
          />
          {uiState.showForm && (
            <BulkFormFieldGrid
              label={fieldsCopies.attachFiles.label}
              field={
                <FileUploaderOutlined
                  attachments={attachments}
                  onFilesChange={handleFilesChange}
                  onRemoveAttachment={handleRemoveAttachment}
                  variant="bulk"
                />
              }
              shouldShowField={uiState.showForm}
            />
          )}
        </Grid>
        <BulkFormFieldGrid
          label={fieldsCopies.smartTags.label}
          hint={fieldsCopies.smartTags.hint}
          field={
            <AutocompleteSelect
              name={BulkDrawerFormFieldsMap.SmartTags.key}
              /**
               * DirtyHack: added props selectedValue=[] and multiple=true in order to avoid
               * autocomplete selected value console warnings and to keep current functionality
               * of selecting an option without setting the currently selected choice as input's
               * value.
               */
              multiple
              selectedValue={[]}
              placeholder={fieldsCopies.smartTags.placeholder}
              placeholderAsLabel={false}
              onSelect={handleSmartTagSelect}
              getOptionDisabled={option => option.value === signatureInfo.selectOption.value}
              defaultOptions={watchers[BulkDrawerFormFieldsMap.SmartTags.key]}
              getOptionLabel={LabelRenderers.nameOrEmpty}
            />
          }
        />
        <When condition={isBulkShowEmailVariantEnabled}>
          <Alert
            action={
              <Button
                color="inherit"
                size="small"
                className={classes.outlookAlertAction}
                href={EMAIL_VARIANT_GENERATOR_URL}
                target="_blank"
                variant="outlined"
              >
                {strings.bulkEmails.forms.emailVariants.action}
              </Button>
            }
            severity="warning"
          >
            {strings.bulkEmails.forms.emailVariants.body}
          </Alert>
        </When>
        <FPTextEditor
          onInit={(e, editor) => {
            editorRef.current = editor;
          }}
          config={tinymceBulkConfig}
          value={watchers.emailBody}
          error={!!errors.emailBody}
          errorText={errors?.emailBody?.message}
          onChange={handleEditorChange}
          onFocus={handleEditorFocus}
        />
        <FPHint description={signatureInfo.helperText} />
      </BulkEmailSectionsCollapser>
      <When condition={shouldComposeBulkSequence}>
        <BulkEmailDrawerAttempts />
      </When>
    </>
  );
};

export default memo(BulkEmailForm);
